import { Box, BoxProps } from '@mui/material';
import { useCarousel } from './hooks';
import { Swiper as CarouselInstance } from 'swiper';
import { useSwiper } from 'swiper/react';
import { useEffect, useState } from 'react';
import { PlayPauseButton, PlayPauseButtonProps } from '@/src/components/playPauseButton/PlayPauseButton';
import { Spinner, SpinnerProps } from '@/src/components/progress/Spinner';

export interface CarouselAutoplayProgressProps extends Omit<BoxProps, 'children' | 'position'> {
  /**
   * Optional Carousel instance from useCarouselInstance hook
   */
  carousel?: CarouselInstance;
  /**
   * Progress Props
   */
  spinnerProps?: Omit<SpinnerProps, 'value'>;
  /**
   * Append style to wrapper box to change position of component
   */
  position?: 'outside' | 'inside';
}

/**
 * Spinner with autoplay progress value
 */
export const CarouselAutoplayProgress = ({
  carousel: carouselProp,
  sx,
  spinnerProps,
  position = 'outside',
  ...rest
}: CarouselAutoplayProgressProps) => {
  const [value, setValue] = useState<number>(0);
  const carouselHook = useSwiper();
  const carousel = carouselProp || carouselHook;

  useEffect(() => {
    // attach listener that controls progress value based on swiper special event
    if (carousel?.autoplay) {
      carousel.on('autoplayTimeLeft', (instance, _timeLeft, percentage) => {
        setValue(instance.autoplay.paused ? 0 : Math.round(100 - percentage * 100));
      });
    }
  }, [!!carousel]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        m: 2,
        ...(position === 'inside' && {
          position: 'absolute',
          zIndex: '90',
          bottom: '0',
          left: '0',
          right: '0',
          m: 2,
        }),
        ...sx,
      }}
      data-testid="CarouselAutoplayProgress"
      {...rest}
    >
      <Spinner value={value} {...spinnerProps} />
    </Box>
  );
};

export interface CarouselAutoplayIconButtonProps extends Partial<PlayPauseButtonProps> {
  /**
   * Optional Carousel instance from useCarouselInstance hook
   */
  carousel?: CarouselInstance;
}

/**
 * IconButton controlling play/paused state of the autoplay
 */
export function CarouselAutoplayIconButton({ carousel: carouselProp, ...rest }: CarouselAutoplayIconButtonProps) {
  const carouselHook = useCarousel();
  const carousel = carouselProp || carouselHook;
  const [value, setValue] = useState<number>(0);

  useEffect(() => {
    // attach listener that controls progress value based on swiper special event
    if (carousel?.autoplay) {
      carousel.on('autoplayTimeLeft', (instance, _timeLeft, percentage) => {
        setValue(instance.autoplay.paused ? 0 : Math.round(100 - percentage * 100));
      });
    }
  }, [!!carousel]);

  return (
    <PlayPauseButton
      aria-label={carousel?.autoplay?.running ? 'Pause carousel autoplay' : 'Play carousel autoplay'}
      isPlaying={!!carousel?.autoplay?.running}
      progress={value}
      onClick={() => {
        carousel?.autoplay?.[carousel?.autoplay?.running ? 'stop' : 'start']();
      }}
      {...rest}
    />
  );
}
